/* eslint-disable @typescript-eslint/no-namespace */
import type { Utility } from '../../types/utility';

export namespace Link {
  /** Link Component Variant Keys */
  export type VariantKeys =
    | 'primary'
    | 'primaryAlternate'
    | 'primaryInverse'
    | 'secondary'
    | 'secondaryAlternate'
    | 'secondaryInverse'
    | 'navLink'
    | 'subNavLink'
    | 'cardOutlineLink';

  /** Link Component Variants */
  export type Variants = {
    [Key in VariantKeys]: Utility.CSSProperties;
  };

  /** Link Component Variant Modes */
  export type VariantModes = Utility.VariantKeyModes<VariantKeys>;
}

const linkBase: Utility.CSSProperties = {
  display: 'inline-block',
  textDecoration: 'none',
  cursor: 'pointer',
};

const linkOutline: Utility.CSSProperties = {
  '&:focus, &:focus-visible': {
    outline: 'none',
    position: 'relative',
    '&::before': {
      content: '""',
      position: 'absolute',
      left: '-4px',
      right: '-4px',
      top: '-6px',
      bottom: '-6px',
      borderStyle: 'solid',
      borderWidth: '1px',
      borderRadius: 'r2',
    },
  },
};

const navLinkShared: Utility.CSSProperties = {
  borderRadius: 'r4',
  width: 'calc(100% + 24px)',
};

const baseVariantShared: Utility.VariantCSSKeys<Link.VariantKeys> = {
  primary: {
    ...linkBase,
    ...linkOutline,
    color: 'hyperlink',
    fontWeight: 'fw500',
    '.hasArrowIcon path': {
      stroke: 'coolGrey.100',
      strokeWidth: '1px',
    },
    '.hasCarotIcon path': {
      strokeWidth: '2px',
    },
    '&:hover, &:focus, &:active': {
      fontWeight: 'fw400',
      '.hasArrowIcon path': {
        stroke: 'initial',
        strokeWidth: 'initial',
      },
      '.hasCarotIcon path': {
        strokeWidth: '1px',
      },
    },
    '&[data-link-as-button=true]': {
      fontWeight: 'fw500',
      '.hasArrowIcon path': {
        stroke: 'white',
      },
      '&:hover, &:focus, &:active': {
        fontWeight: 'fw500',
        '.hasArrowIcon path': {
          stroke: 'coolGrey.100',
        },
      },
    },
  },
  primaryAlternate: {
    ...linkBase,
    ...linkOutline,
    color: 'coolGrey.100',
    fontWeight: 'fw500',
    '.hasArrowIcon path': {
      stroke: 'coolGrey.100',
      strokeWidth: '1px',
    },
    '.hasCarotIcon path': {
      stroke: 'coolGrey.100',
      strokeWidth: '2px',
    },
    '&:hover, &:focus, &:active': {
      fontWeight: 'fw400',
      '.hasArrowIcon path': {
        stroke: 'initial',
        strokeWidth: 'initial',
      },
      '.hasCarotIcon path': {
        strokeWidth: '1px',
      },
      '&[data-link-as-button=true]': {
        fontWeight: 'fw500',
      },
    },
  },
  primaryInverse: {
    ...linkBase,
    ...linkOutline,
    color: 'white',
    fontWeight: 'fw500',
    '.hasArrowIcon path': {
      stroke: 'white',
      strokeWidth: '1px',
    },
    '.hasCarotIcon path': {
      stroke: 'white',
      strokeWidth: '2px',
    },
    '&:hover, &:focus, &:active': {
      fontWeight: 'fw400',
      '.hasArrowIcon path': {
        stroke: 'initial',
        strokeWidth: 'initial',
      },
      '.hasCarotIcon path': {
        strokeWidth: '1px',
      },
      '&[data-link-as-button=true]': {
        fontWeight: 'fw500',
      },
    },
  },
  secondary: {
    ...linkBase,
    ...linkOutline,
    color: 'hyperlink',
    fontWeight: 'fw400',
    '.hasArrowIcon path': {
      stroke: 'coolGrey.100',
      strokeWidth: '1px',
    },
    '&:hover, &:focus, &:active': {
      fontWeight: 'fw500',
      '.hasArrowIcon path': {
        stroke: 'coolGrey.100',
        strokeWidth: '1px',
      },
      '.hasCarotIcon path': {
        strokeWidth: '2px',
      },
      '&[data-link-as-button=true]': {
        fontWeight: 'fw500',
      },
    },
  },
  secondaryAlternate: {
    ...linkBase,
    ...linkOutline,
    color: 'coolGrey.100',
    fontWeight: 'fw400',
    '&:hover, &:focus, &:active': {
      fontWeight: 'fw500',
      '.hasArrowIcon path': {
        stroke: 'coolGrey.100',
        strokeWidth: '1px',
      },
      '.hasCarotIcon path': {
        strokeWidth: '2px',
      },
      '&[data-link-as-button=true]': {
        fontWeight: 'fw500',
      },
    },
  },
  secondaryInverse: {
    ...linkBase,
    ...linkOutline,
    color: 'white',
    fontWeight: 'fw400',
    '.hasCarotIcon path': {
      stroke: 'white',
      strokeWidth: '1px',
    },
    '&:hover, &:focus, &:active': {
      fontWeight: 'fw500',
      '.hasArrowIcon path': {
        stroke: 'white',
        strokeWidth: '1px',
      },
      '.hasCarotIcon path': {
        strokeWidth: '2px',
      },
      '&[data-link-as-button=true]': {
        fontWeight: 'fw500',
      },
    },
  },
  navLink: {
    ...linkBase,
    ...navLinkShared,
    ...linkOutline,
    display: 'flex',
    alignItems: 'center',
    fontWeight: 'fw400',
    textDecoration: 'none',
    backgroundColor: 'transparent',
    color: 'coolGrey.90',
    height: '42px',
    margin: 0,
    '&:active, &:hover, &:focus': {
      ...navLinkShared,
      paddingLeft: '12px',
      marginLeft: '-12px',
      backgroundColor: 'coolGrey.5',
    },
  },
  subNavLink: {
    ...linkBase,
    ...navLinkShared,
    ...linkOutline,
    lineHeight: 'lh1',
    display: 'flex',
    alignItems: 'center',
    fontWeight: 'fw400',
    textDecoration: 'none',
    backgroundColor: 'transparent',
    color: 'coolGrey.90',
    minHeight: '40px',
    margin: 0,
    '&:focus': {
      ...navLinkShared,
      outline: 'none',
      outlineColor: 'inherit',
      outlineStyle: 'solid',
    },
    '&:active, &:hover, &:focus': {
      ...navLinkShared,
      '&::before': {
        ...linkOutline,
        left: '-16px',
      },
    },
  },
  cardOutlineLink: {
    ...linkBase,
    color: 'hyperlink',
    fontWeight: 'fw500',
    '.hasArrowIcon path': {
      stroke: 'coolGrey.100',
      strokeWidth: '1px',
    },
    '.hasCarotIcon path': {
      strokeWidth: '2px',
    },
    '&:hover, &:focus, &:active': {
      fontWeight: 'fw400',
      '.hasArrowIcon path': {
        stroke: 'initial',
        strokeWidth: 'initial',
      },
      '.hasCarotIcon path': {
        strokeWidth: '1px',
      },
    },
    '&[data-link-as-button=true]': {
      fontWeight: 'fw500',
      '.hasArrowIcon path': {
        stroke: 'white',
      },
      '&:hover, &:focus, &:active': {
        fontWeight: 'fw500',
        '.hasArrowIcon path': {
          stroke: 'coolGrey.100',
        },
      },
    },
    '&:focus, &:focus-visible': {
      outline: 'none',
    },
    '&:focus-visible': {
      '&::after': {
        position: 'absolute',
        content: '""',
        top: 0,
        bottom: 0,
        left: 0,
        right: 0,
        outlineStyle: 'dashed',
        outlineColor: 'primary',
        outlineWidth: '2px',
        outlineOffset: '8px',
      },
    },
  },
};

const chart: Utility.VariantCSSKeys<Link.VariantKeys> = {
  ...baseVariantShared,
  navLink: {
    ...baseVariantShared.navLink,
    '&:focus': {
      outlineColor: 'orange.70',
    },
    '&:hover, &:focus-visible': {
      color: 'orange.70',
    },
  },
  subNavLink: {
    ...baseVariantShared.subNavLink,
    '&:focus': {
      outlineColor: 'orange.70',
    },
    '&:hover, &:focus-visible': {
      color: 'orange.70',
    },
  },
};

const co: Utility.VariantCSSKeys<Link.VariantKeys> = {
  ...baseVariantShared,
  navLink: {
    ...baseVariantShared.navLink,
    '&:focus': {
      outlineColor: 'blue.70',
    },
    '&:hover, &:focus-visible': {
      color: 'blue.70',
    },
  },
  subNavLink: {
    ...baseVariantShared.subNavLink,
    '&:focus': {
      outlineColor: 'blue.70',
    },
    '&:hover, &:focus-visible': {
      color: 'blue.70',
    },
  },
};

const edu: Utility.VariantCSSKeys<Link.VariantKeys> = {
  ...baseVariantShared,
  navLink: {
    ...baseVariantShared.navLink,
    '&:focus': {
      outlineColor: 'indigo.70',
    },
    '&:hover, &:focus-visible': {
      color: 'indigo.70',
    },
  },
  subNavLink: {
    ...baseVariantShared.subNavLink,
    '&:focus': {
      outlineColor: 'indigo.70',
    },
    '&:hover, &:focus-visible': {
      color: 'indigo.70',
    },
  },
};

const scale: Utility.VariantCSSKeys<Link.VariantKeys> = {
  ...baseVariantShared,
  navLink: {
    ...baseVariantShared.navLink,
    '&:focus': {
      outlineColor: 'indigo.70',
    },
    '&:hover, &:focus-visible': {
      color: 'indigo.70',
    },
  },
  subNavLink: {
    ...baseVariantShared.subNavLink,
    '&:focus': {
      outlineColor: 'indigo.70',
    },
    '&:hover, &:focus-visible': {
      color: 'indigo.70',
    },
  },
};

const spark: Utility.VariantCSSKeys<Link.VariantKeys> = {
  ...baseVariantShared,
  navLink: {
    ...baseVariantShared.navLink,
    '&:focus': {
      outlineColor: 'red.70',
    },
    '&:hover, &:focus-visible': {
      color: 'red.70',
    },
  },
  subNavLink: {
    ...baseVariantShared.subNavLink,
    '&:focus': {
      outlineColor: 'red.70',
    },
    '&:hover, &:focus-visible': {
      color: 'red.70',
    },
  },
};

const link: Utility.VariantKeyModes<Link.VariantKeys> = {
  products: {
    chart,
    chartblog: chart,
    co,
    edu,
    scale,
    spark,
  },
};

export const linkVariants = Object.keys(chart) as Link.VariantKeys[];

export default link;
